import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

/**
 * BookingPage — /book/:slug
 * Customer selects service, date, slot, enters name/phone → books appointment
 */

const API_BASE = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

export default function BookingPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get("service") || null;
  const preselectedServiceId = searchParams.get("serviceId") || null;
  const preselectedPrice = searchParams.get("price") || null;

  const [catalogue, setCatalogue] = useState(null);
  const [business, setBusiness] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Booking form state
  const [step, setStep] = useState(1); // 1: service, 2: date+slot, 3: details, 4: confirm
  const [selectedService, setSelectedService] = useState(preselectedService ? {
    name: preselectedService, itemId: preselectedServiceId, price: preselectedPrice ? Number(preselectedPrice) : null
  } : null);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [note, setNote] = useState("");
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);

  // Load catalogue
  useEffect(() => {
    fetch(`${API_BASE}/catalogue/${slug}`)
      .then(r => r.json())
      .then(d => {
        setCatalogue(d.catalogue);
        setBusiness(d.business);
        setItems(d.items || []);
        setLoading(false);
        if (preselectedService) setStep(2);
      })
      .catch(() => { setError("Could not load shop"); setLoading(false); });
  }, [slug, preselectedService]);

  // Load slots when date selected
  useEffect(() => {
    if (!selectedDate || !business) return;
    setSlotsLoading(true);
    setSlots([]);
    setSelectedSlot(null);
    fetch(`${API_BASE}/bookings/slots?businessId=${business.businessId}&date=${selectedDate}`)
      .then(r => r.json())
      .then(d => { setSlots(d.slots || []); setSlotsLoading(false); })
      .catch(() => setSlotsLoading(false));
  }, [selectedDate, business]);

  const handleBook = async () => {
    if (!customerName.trim()) return;
    setBooking(true);
    try {
      const res = await fetch(`${API_BASE}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId: business.businessId,
          catalogueId: catalogue.catalogueId,
          serviceId: selectedService?.itemId || "",
          serviceName: selectedService?.name || "Appointment",
          servicePrice: selectedService?.price || null,
          date: selectedDate,
          time: selectedSlot.time,
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          note: note.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Booking failed");
      setBooked(data);
      // Save to localStorage
      localStorage.setItem(`qkard_booking_${slug}`, JSON.stringify({
        bookingId: data.bookingId,
        timeLabel: data.timeLabel,
        date: data.date,
        serviceName: data.serviceName,
        customerName: customerName.trim(),
        savedAt: new Date().toISOString(),
      }));
    } catch (e) {
      alert(e.message || "Could not book. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  // ── Date options — today + next 7 days ───────────────────────────────────
  const dateOptions = [];
  for (let i = 0; i <= 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const val = d.toISOString().split("T")[0];
    const label = i === 0 ? "Today" : i === 1 ? "Tomorrow"
      : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    dateOptions.push({ val, label });
  }

  const accent = catalogue?.accentColor || "#6c63ff";

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 12, fontFamily: "sans-serif", color: "#888" }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#111" }}>
        qk<span style={{ color: "#FF6B35" }}>a</span>rd
      </div>
      Loading…
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, textAlign: "center", fontFamily: "sans-serif", color: "#888" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
      <div>{error}</div>
    </div>
  );

  // ── Booked confirmation ───────────────────────────────────────────────────
  if (booked) return (
    <div style={{ ...p.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", textAlign: "center", padding: 24 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#111", marginBottom: 8 }}>Appointment Booked!</div>
      <div style={{ fontSize: 14, color: "#888", marginBottom: 24 }}>{business?.businessName}</div>

      <div style={{ background: "#f9f9ff", border: `2px solid ${accent}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 320, marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Service</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 16 }}>{booked.serviceName}</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Date & Time</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: accent, marginBottom: 4 }}>{booked.timeLabel}</div>
        <div style={{ fontSize: 14, color: "#555" }}>
          {new Date(booked.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>

      <div style={{ fontSize: 13, color: "#aaa", marginBottom: 24 }}>
        Booking ID: <strong>{booked.bookingId.slice(-8).toUpperCase()}</strong>
      </div>

      <button
        style={{ ...p.btn, background: accent, width: "100%", maxWidth: 320 }}
        onClick={() => window.location.href = `/c/${slug}`}
      >
        Back to Shop
      </button>
    </div>
  );

  // ── Booking steps ─────────────────────────────────────────────────────────
  return (
    <div style={p.page}>
      {/* Header */}
      <div style={{ ...p.header, borderBottom: `3px solid ${accent}` }}>
        <button style={p.backBtn} onClick={() => step > 1 ? setStep(s => s - 1) : window.history.back()}>
          ‹ Back
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>{business?.businessName}</div>
          <div style={{ fontSize: 12, color: "#888" }}>Book Appointment</div>
        </div>
        <div style={{ width: 48 }} />
      </div>

      {/* Progress dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, padding: "14px 0" }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            width: i === step ? 24 : 8, height: 8, borderRadius: 4,
            background: i <= step ? accent : "#e5e5e5",
            transition: "all 0.2s",
          }} />
        ))}
      </div>

      <div style={{ padding: "0 16px 40px" }}>

        {/* Step 1 — Select service */}
        {step === 1 && (
          <>
            <div style={p.stepTitle}>What service do you need?</div>
            {items.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
                No services listed yet
              </div>
            ) : (
              items.filter(i => i.inStock !== false).map(item => (
                <div
                  key={item.itemId}
                  style={{
                    ...p.serviceCard,
                    border: `2px solid ${selectedService?.itemId === item.itemId ? accent : "#efefed"}`,
                    background: selectedService?.itemId === item.itemId ? "#f9f9ff" : "#fff",
                  }}
                  onClick={() => {
                    setSelectedService({ name: item.name, itemId: item.itemId, price: item.price });
                    setStep(2);
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#111" }}>{item.name}</div>
                    {item.description && <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>{item.description}</div>}
                  </div>
                  {item.price && (
                    <div style={{ fontSize: 16, fontWeight: 700, color: accent }}>₹{item.price}</div>
                  )}
                </div>
              ))
            )}
            <div style={{ marginTop: 16 }}>
              <div
                style={{ ...p.serviceCard, border: "2px dashed #e5e5e5" }}
                onClick={() => { setSelectedService({ name: "Appointment", itemId: "", price: null }); setStep(2); }}
              >
                <div style={{ fontSize: 14, color: "#888" }}>+ Other / General appointment</div>
              </div>
            </div>
          </>
        )}

        {/* Step 2 — Select date + slot */}
        {step === 2 && (
          <>
            <div style={p.stepTitle}>Pick a date</div>

            {/* Date pills */}
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, marginBottom: 20, scrollbarWidth: "none" }}>
              {dateOptions.map(d => (
                <button
                  key={d.val}
                  style={{
                    flexShrink: 0, padding: "10px 16px", borderRadius: 12,
                    border: "1.5px solid",
                    borderColor: selectedDate === d.val ? accent : "#e5e5e5",
                    background: selectedDate === d.val ? accent : "#fff",
                    color: selectedDate === d.val ? "#fff" : "#444",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => setSelectedDate(d.val)}
                >
                  {d.label}
                </button>
              ))}
            </div>

            {selectedDate && (
              <>
                <div style={p.stepTitle}>Pick a time</div>
                {slotsLoading ? (
                  <div style={{ textAlign: "center", padding: 24, color: "#aaa" }}>Loading slots…</div>
                ) : slots.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 24, color: "#aaa" }}>
                    No slots available for this date
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
                    {slots.map(slot => (
                      <button
                        key={slot.slotKey}
                        disabled={slot.isBooked}
                        style={{
                          padding: "12px 8px", borderRadius: 12,
                          border: "1.5px solid",
                          borderColor: slot.isBooked ? "#f0f0f0"
                            : selectedSlot?.slotKey === slot.slotKey ? accent : "#e5e5e5",
                          background: slot.isBooked ? "#f9f9f9"
                            : selectedSlot?.slotKey === slot.slotKey ? accent : "#fff",
                          color: slot.isBooked ? "#ccc"
                            : selectedSlot?.slotKey === slot.slotKey ? "#fff" : "#333",
                          fontSize: 13, fontWeight: 600, cursor: slot.isBooked ? "not-allowed" : "pointer",
                          textDecoration: slot.isBooked ? "line-through" : "none",
                        }}
                        onClick={() => !slot.isBooked && setSelectedSlot(slot)}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                )}

                {selectedSlot && (
                  <button
                    style={{ ...p.btn, background: accent, width: "100%" }}
                    onClick={() => setStep(3)}
                  >
                    Continue →
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* Step 3 — Customer details */}
        {step === 3 && (
          <>
            <div style={p.stepTitle}>Your details</div>

            {/* Summary */}
            <div style={{ background: "#f9f9ff", border: `1.5px solid ${accent}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: "#888" }}>Booking summary</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginTop: 4 }}>
                {selectedService?.name}
              </div>
              <div style={{ fontSize: 14, color: accent, fontWeight: 600, marginTop: 2 }}>
                {selectedSlot?.label} · {new Date(selectedDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
              </div>
              {selectedService?.price && (
                <div style={{ fontSize: 14, color: "#555", marginTop: 2 }}>₹{selectedService.price}</div>
              )}
            </div>

            <label style={p.label}>Your name *</label>
            <input
              style={p.input}
              placeholder="e.g. Rahul Kumar"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              maxLength={50}
            />

            <label style={p.label}>Phone number</label>
            <input
              style={p.input}
              type="tel"
              placeholder="e.g. 9876543210"
              value={customerPhone}
              onChange={e => setCustomerPhone(e.target.value)}
              maxLength={15}
            />

            <label style={p.label}>Note (optional)</label>
            <input
              style={p.input}
              placeholder="Any special request"
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={100}
            />

            <button
              style={{ ...p.btn, background: customerName.trim() ? accent : "#ccc", width: "100%", marginTop: 8 }}
              onClick={handleBook}
              disabled={!customerName.trim() || booking}
            >
              {booking ? "Booking…" : "✅ Confirm Appointment"}
            </button>
          </>
        )}

      </div>
    </div>
  );
}

const p = {
  page: { minHeight: "100vh", background: "#f5f5f3", fontFamily: "'DM Sans','Inter',sans-serif" },
  header: {
    background: "#fff", padding: "0 16px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    height: 56, position: "sticky", top: 0, zIndex: 10,
  },
  backBtn: {
    background: "none", border: "none",
    fontSize: 20, color: "#444", cursor: "pointer", width: 48,
  },
  stepTitle: { fontSize: 17, fontWeight: 700, color: "#111", marginBottom: 14, marginTop: 4 },
  serviceCard: {
    background: "#fff", borderRadius: 14, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 10, cursor: "pointer",
    transition: "border-color 0.15s",
  },
  btn: {
    padding: 15, borderRadius: 12, border: "none",
    color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer",
  },
  label: { fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, display: "block" },
  input: {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: "1.5px solid #e5e5e5", fontSize: 15, outline: "none",
    boxSizing: "border-box", marginBottom: 14, background: "#fff",
  },
};
